/**
 * Pantalla temporal de Sprint 0.
 * Verifica que el stack funciona: React + TS + Tailwind + design tokens.
 * En Sprint 1-3 se reemplaza por las pantallas reales (S1, F1, etc.).
 */
import { useQuery } from "@tanstack/react-query";

interface HealthResponse {
  status: string;
  name: string;
  version: string;
  environment: string;
  timestamp: string;
  checks: Record<string, string>;
}

async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch("/api/health");
  if (!res.ok) {
    throw new Error(`Health check failed: ${res.status}`);
  }
  return res.json() as Promise<HealthResponse>;
}

export function HelloKaptar() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["health"],
    queryFn: fetchHealth,
    retry: false,
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Kaptar</h1>
        </div>

        <h2 className="mb-2 text-xl font-bold text-slate-900">Hello Kaptar 👋</h2>
        <p className="mb-8 text-slate-600">
          Frontend scaffolded. El design system está cargado (Plus Jakarta Sans, primary azul{" "}
          <code className="rounded bg-brand-50 px-1 text-brand-700">#2563eb</code>, CTA emerald{" "}
          <code className="rounded bg-cta-50 px-1 text-cta-600">#059669</code>). Próximo paso:
          Sprint 1.
        </p>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-xs">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Conexión con backend</h3>
            {isLoading && <span className="text-xs text-slate-500">consultando...</span>}
            {data && (
              <span className="rounded-full bg-cta-100 px-2 py-0.5 text-xs font-semibold text-cta-700">
                ✓ {data.status}
              </span>
            )}
            {isError && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                ✗ backend no disponible
              </span>
            )}
          </div>

          {data && (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <dt className="text-slate-500">Nombre</dt>
              <dd className="font-medium text-slate-900">{data.name}</dd>
              <dt className="text-slate-500">Versión</dt>
              <dd className="font-mono text-slate-900">{data.version}</dd>
              <dt className="text-slate-500">Entorno</dt>
              <dd className="font-medium text-slate-900">{data.environment}</dd>
              <dt className="text-slate-500">DB</dt>
              <dd className="font-mono text-slate-900">{data.checks.database ?? "—"}</dd>
            </dl>
          )}

          {isError && (
            <p className="text-sm text-slate-600">
              Levanta el backend con <code className="rounded bg-slate-100 px-1">make dev</code>{" "}
              desde la raíz del repo.
            </p>
          )}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            type="button"
          >
            Botón Primary (brand)
          </button>
          <button
            className="rounded-md bg-cta-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-600"
            type="button"
          >
            Botón CTA (emerald)
          </button>
        </div>
      </div>
    </main>
  );
}
